import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../config/axios';
import toast from 'react-hot-toast';
import {
  ChatBubbleLeftRightIcon,
  PlusIcon,
  PencilSquareIcon,
  XMarkIcon,
  SparklesIcon,
  Squares2X2Icon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';

const Typebot = () => {
  const [workspace, setWorkspace] = useState(null);
  const [typebots, setTypebots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showWorkspaceModal, setShowWorkspaceModal] = useState(false);
  const [newTypebot, setNewTypebot] = useState({ name: '', description: '' });
  const [newWorkspace, setNewWorkspace] = useState({ name: '' });
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [workspaceRes, typebotsRes, subscriptionRes] = await Promise.all([
        api.get('/typebot/workspace'),
        api.get('/typebot/typebots'),
        api.get('/subscriptions/current'),
      ]);

      setWorkspace(workspaceRes.data.workspace);
      setTypebots(typebotsRes.data.typebots);
      setSubscription(subscriptionRes.data.subscription);
    } catch (error) {
      console.error('Error fetching Chatbot data:', error);
      toast.error('Failed to load Chatbot data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWorkspace = async () => {
    try {
      if (!subscription || subscription.status !== 'active') {
        toast.error('You need an active subscription to create a workspace');
        return;
      }

      await api.post('/typebot/workspace', {
        workspaceName: newWorkspace.name,
        subscriptionId: subscription.id,
      });
      
      toast.success('Chatbot workspace created successfully!');
      setShowWorkspaceModal(false);
      setNewWorkspace({ name: '' });
      fetchData();
    } catch (error) {
      console.error('Error creating workspace:', error);
      let errorMessage = 'Failed to create workspace';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      toast.error(errorMessage);
    }
  };

  const handleCreateTypebot = async () => {
    try {
      await api.post('/typebot/typebot', newTypebot);
      toast.success('Chatbot created successfully!');
      setShowCreateModal(false);
      setNewTypebot({ name: '', description: '' });
      fetchData();
    } catch (error) {
      console.error('Error creating chatbot:', error);
      toast.error('Failed to create chatbot');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner spinner-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-orange">
            <ChatBubbleLeftRightIcon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="page-title">Chatbot Management</h1>
            <p className="text-sm text-neutral-500">Build and manage your <span className="bracket-highlight">automated conversations</span></p>
          </div>
        </div>
      </div>

      {/* Workspace Section */}
      {workspace ? (
        <div className="card bg-gradient-to-br from-white via-white to-primary-50/30 border border-neutral-200">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Squares2X2Icon className="w-5 h-5 text-primary-500" />
                <h3 className="text-lg font-semibold text-neutral-800">Chatbot Workspace</h3>
              </div>
              <p className="text-sm text-neutral-500">Your central hub for chatbot automation</p>
            </div>
            <span className="badge badge-green self-start">
              {workspace.status}
            </span>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <div className="p-4 bg-neutral-50 rounded-lg">
              <dt className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Workspace Name</dt>
              <dd className="mt-1 text-base font-semibold text-neutral-800">{workspace.name}</dd>
            </div>
          </div>
          
          {/* Usage Statistics Section */}
          {workspace.usage && (
            <div className="border-t border-neutral-200 pt-6 mt-6">
              <div className="flex items-center gap-2 mb-4">
                <ChartBarIcon className="w-5 h-5 text-neutral-500" />
                <h4 className="text-sm font-semibold text-neutral-700">Current Month Usage</h4>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="stat-card !p-4 bg-primary-50 border-primary-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                      <svg className="h-5 w-5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-primary-600">Messages Used</p>
                      <p className="text-xl font-bold text-primary-900">{workspace.usage.messagesUsed}</p>
                    </div>
                  </div>
                </div>

                <div className="stat-card !p-4 bg-green-50 border-green-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                      <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-green-600">Conversations Started</p>
                      <p className="text-xl font-bold text-green-900">{workspace.usage.conversationsStarted}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <p className="mt-4 text-center text-xs text-neutral-500">
                Usage statistics for {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][workspace.usage.currentMonth - 1]} {workspace.usage.currentYear}
              </p>
            </div>
          )}
          
          <div className="mt-6 pt-6 border-t border-neutral-200">
            <p className="text-sm text-neutral-500">
              Workspace ID: <span className="font-mono text-neutral-700">{workspace.workspaceId}</span>
            </p>
          </div>
        </div>
      ) : (
        <div className="card text-center py-12">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-neutral-100 flex items-center justify-center mb-4">
            <ChatBubbleLeftRightIcon className="w-8 h-8 text-neutral-400" />
          </div>
          <h3 className="text-lg font-semibold text-neutral-800">No workspace yet</h3>
          <p className="mt-2 text-sm text-neutral-500 max-w-sm mx-auto">
            Create a Chatbot workspace to start building automated conversations with your customers.
          </p>
          <div className="mt-6">
            <button
              onClick={() => setShowWorkspaceModal(true)}
              className="btn-primary inline-flex items-center gap-2"
            >
              <PlusIcon className="h-4 w-4" />
              Create Workspace
            </button>
          </div>
          {(!subscription || subscription.status !== 'active') && (
            <div className="mt-6 mx-auto max-w-md">
              <div className="alert alert-warning">
                <div className="flex items-start gap-3">
                  <svg className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-yellow-800">
                    You need an active subscription to create a workspace. <a href="/subscriptions" className="font-medium underline">Subscribe now</a>
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Chatbots Section */}
      {workspace && (
        <div className="card">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h3 className="text-lg font-semibold text-neutral-800">Your Chatbots</h3>
              <p className="text-sm text-neutral-500 mt-1">Manage your automated conversation flows</p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary btn-sm inline-flex items-center gap-2 self-start"
            >
              <PlusIcon className="h-4 w-4" />
              Create Chatbot
            </button>
          </div>

          {typebots.length === 0 ? (
            <div className="text-center py-12 bg-neutral-50 rounded-xl border-2 border-dashed border-neutral-200">
              <SparklesIcon className="mx-auto h-10 w-10 text-neutral-400" />
              <h4 className="mt-3 text-sm font-semibold text-neutral-800">No chatbots yet</h4>
              <p className="mt-1 text-sm text-neutral-500">
                Create your first chatbot to get started with automation.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {typebots.map((typebot) => (
                <div 
                  key={typebot.id} 
                  className="group p-5 border border-neutral-200 rounded-xl bg-white hover:border-primary-300 hover:shadow-md transition-all duration-300"
                >
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">{typebot.icon || '🤖'}</div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-neutral-800 truncate group-hover:text-primary-600 transition-colors">
                        {typebot.name}
                      </h4>
                      <p className="mt-0.5 text-xs text-neutral-500 line-clamp-2">
                        {typebot.description || 'No description'}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <Link
                      to={`/typebot/builder/${typebot.id}`}
                      className="btn-outline btn-sm w-full inline-flex items-center justify-center gap-2"
                    >
                      <PencilSquareIcon className="h-3 w-3" />
                      Open in Builder
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create Workspace Modal */}
      {showWorkspaceModal && (
        <div className="modal-container">
          <div className="modal-overlay" onClick={() => setShowWorkspaceModal(false)} />
          <div className="modal-content max-w-md">
            <div className="modal-header flex items-center justify-between">
              <h3 className="text-lg font-semibold text-neutral-800">Create New Workspace</h3>
              <button
                onClick={() => setShowWorkspaceModal(false)}
                className="p-2 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="modal-body space-y-4">
              <div>
                <label htmlFor="workspaceName" className="input-label">
                  Workspace Name
                </label>
                <input
                  type="text"
                  id="workspaceName"
                  value={newWorkspace.name}
                  onChange={(e) => setNewWorkspace({ ...newWorkspace, name: e.target.value })}
                  className="input-field"
                  placeholder="My Chatbot Workspace"
                />
              </div>
              <div className="p-4 bg-primary-50 rounded-lg border border-primary-100">
                <div className="flex items-start gap-3">
                  <ChatBubbleLeftRightIcon className="h-5 w-5 text-primary-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-primary-700">
                    This will create a new Chatbot workspace where you can build and manage your chatbots.
                  </p>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowWorkspaceModal(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateWorkspace}
                disabled={!newWorkspace.name.trim() || !subscription || subscription.status !== 'active'}
                className="btn-primary"
              >
                Create Workspace
              </button>
            </div>
            {(!subscription || subscription.status !== 'active') && (
              <div className="px-6 pb-6">
                <div className="alert alert-warning">
                  <p className="text-sm">You need an active subscription to create a workspace.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create Chatbot Modal */}
      {showCreateModal && (
        <div className="modal-container">
          <div className="modal-overlay" onClick={() => setShowCreateModal(false)} />
          <div className="modal-content max-w-md">
            <div className="modal-header flex items-center justify-between">
              <h3 className="text-lg font-semibold text-neutral-800">Create New Chatbot</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-2 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="modal-body space-y-4">
              <div>
                <label htmlFor="name" className="input-label">
                  Chatbot Name
                </label>
                <input
                  type="text"
                  id="name"
                  value={newTypebot.name}
                  onChange={(e) => setNewTypebot({ ...newTypebot, name: e.target.value })}
                  className="input-field"
                  placeholder="Customer Support Bot"
                />
              </div>
              <div>
                <label htmlFor="description" className="input-label">
                  Description
                </label>
                <textarea
                  id="description"
                  value={newTypebot.description}
                  onChange={(e) => setNewTypebot({ ...newTypebot, description: e.target.value })}
                  rows={3}
                  className="input-field resize-none"
                  placeholder="Describe what this chatbot does..."
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowCreateModal(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTypebot}
                disabled={!newTypebot.name.trim()}
                className="btn-primary"
              >
                Create Chatbot
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Typebot;
